import { useMemo, useState } from 'react';

import { Dialog, DialogContent, Grid, Typography } from '@mui/material';

import { navigate } from '@redwoodjs/router';
import { MetaTags, useMutation, useQuery } from '@redwoodjs/web';

import Loader from 'src/components/Loader/Loader';
import ModalContent from 'src/components/ModalContent/ModalContent';
import TableList from 'src/components/TableList/TableList';
import { showErrorToast, showSuccessToast } from 'src/components/Toasts/toast';
import StatusChip from 'src/components/UserStatusChip/UserStatusChip';
import { DEFAULT_BORDER, PRODUCTS_TABLE_HEADERS } from 'src/constants';
import { DELETE_PRODUCT_MUTATION } from 'src/graphql/mutations/products.mutation';
import { GET_PRODUCTS } from 'src/graphql/queries/product.query';
import {
    filteredProductsTableData,
    FilterOptionsType,
    updateTableFilters,
} from 'src/utils/tableUtils';

const ProductsPage = () => {
    const [openDeleteModal, setOpenDeleteModal] = useState(false);
    const [recordId, setRecordId] = useState<number | null>();
    const [tableFilters, setTableFilters] = useState({});

    const { data, loading } = useQuery(GET_PRODUCTS, {
        fetchPolicy: 'cache-and-network',
        onError: () => {
            showErrorToast('There was an error retrieving products');
        },
    });

    const tableData = useMemo(() => {
        return filteredProductsTableData(data, tableFilters).map((product) => {
            return {
                ...product,
                statusChip: <StatusChip status={product?.status} type="productsCatalogue" />,
            };
        });
    }, [data, tableFilters]);

    const filterOptions = () => {
        const products = data?.products ?? [];
        return Object.fromEntries([
            [
                'Product Name',
                Array.from(
                    new Set(
                        products.map(({ name }) => {
                            return name;
                        })
                    )
                ).map((name) => {
                    return {
                        value: name,
                        label: name,
                    };
                }),
            ],
            [
                'Product Type',
                Array.from(
                    new Set(
                        products.map(({ type: productType }) => {
                            return productType;
                        })
                    )
                ).map((productType) => {
                    return {
                        value: productType,
                        label: productType,
                    };
                }),
            ],
            [
                'Primary/Secondary',
                Array.from(
                    new Set(
                        products.map(({ catalogue }) => {
                            return catalogue;
                        })
                    )
                ).map((catalogue) => {
                    return {
                        value: catalogue,
                        label: catalogue,
                    };
                }),
            ],
            [
                'Status',
                Array.from(
                    new Set(
                        products.map(({ status }) => {
                            return status;
                        })
                    )
                ).map((status) => {
                    return {
                        value: status,
                        label: status,
                    };
                }),
            ],
        ]) as FilterOptionsType;
    };

    const [deleteProduct] = useMutation(DELETE_PRODUCT_MUTATION, {
        onCompleted: (deleteData) => {
            showSuccessToast(`${deleteData.deleteProduct.name} deleted succefully`);
        },
        refetchQueries: [
            {
                query: GET_PRODUCTS,
            },
        ],
        onError: (error) => {
            showErrorToast(error.message ?? 'Error Deleting the product');
        },
    });

    const handleAddRecord = () => {
        navigate('/products/add');
    };

    const handleViewRecord = ({ id }) => {
        navigate(`/products/${id}`);
    };

    const handleOpenDeleteModal = (id) => {
        setRecordId(id);
        setOpenDeleteModal(true);
    };

    const handleCloseDeleteModal = () => {
        setOpenDeleteModal(false);
    };

    const handleDeleteRecord = () => {
        deleteProduct({ variables: { id: recordId } });
        handleCloseDeleteModal();
    };

    return (
        <>
            <MetaTags title="Products" description="Product list page" />

            <Grid container sx={{ gap: '24px' }}>
                <Grid
                    item
                    xs={12}
                    sx={{
                        ...DEFAULT_BORDER,
                        height: '116px',
                        backgroundColor: '#FFFFFF',
                        boxShadow: '0px 4px 20px rgba(38, 50, 56, 0.1)',
                        padding: '8px 24px',
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'center',
                    }}
                >
                    <Typography sx={{ fontSize: '24px', fontWeight: 700, color: '#1D1D1B' }}>
                        Product Catalogue
                    </Typography>
                    <Typography>
                        Add, View, Remove and update Product Catalogue as necessary
                    </Typography>
                </Grid>
                <Grid item xs={12}>
                    {loading ? (
                        <Loader />
                    ) : (
                        <TableList
                            addRecordButtonText="Add Product"
                            data={tableData}
                            headers={PRODUCTS_TABLE_HEADERS}
                            title="Products List"
                            addRecordCallBack={handleAddRecord}
                            handleViewRecord={handleViewRecord}
                            deleteRecordCallBack={handleOpenDeleteModal}
                            enableFilter
                            enableExport
                            filterOptions={filterOptions()}
                            updateTableFilters={(filter: string, newFilterValue: string) => {
                                return setTableFilters((prevFilters) => {
                                    return updateTableFilters(prevFilters, newFilterValue, filter);
                                });
                            }}
                        />
                    )}

                    <Dialog
                        open={openDeleteModal}
                        onClose={handleCloseDeleteModal}
                        aria-labelledby="delete-user-dialog-title"
                        sx={{
                            '& .MuiDialog-paper': {
                                width: '400px',
                            },
                        }}
                    >
                        <DialogContent>
                            <ModalContent
                                handleClose={handleCloseDeleteModal}
                                handleAction={handleDeleteRecord}
                                header="Delete Product?"
                                message="Are you sure you want to permanently delete this product record?"
                                buttonText="Delete"
                            />
                        </DialogContent>
                    </Dialog>
                </Grid>
            </Grid>
        </>
    );
};

export default ProductsPage;
